import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, renderSlot as _renderSlot, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = ["aria-label"]

import { computed, ref, reactive, onMounted, onUnmounted, watch, nextTick } from "vue"
import { addListener, removeListener } from 'resize-detector'


export default /*#__PURE__*/_defineComponent({
  __name: 'ClampText',
  props: {
    text: {},
    maxHeight: {},
    maxLines: {},
    expanded: { type: Boolean, default: false },
    location: { default: 'end' },
    ellipsis: { default: '…' },
    autoResize: { type: Boolean, default: true }
  },
  emits: ["clamp-change", "update:expanded"],
  setup(__props: any, { emit: emits }) {

const props = __props;




const textClampRef = ref<HTMLElement | null>(null)
const contentRef = ref<HTMLElement | null>(null)
const textRef = ref<HTMLElement | null>(null)
const state = reactive({
  offset: 0,
  localExpanded: !!props.expanded,
})

const realMaxHeight = computed(() => {
  if (state.localExpanded) return undefined
  if (!props.maxHeight) return undefined
  return typeof props?.maxHeight === 'number' ? `${props?.maxHeight}px` : props?.maxHeight
})

const applyChange = () => {
  textRef.value && (textRef.value.textContent = realText.value)
}
const update = () => {
  if (state.localExpanded) return
  applyChange()
  if (isOverflow() || isClamped.value) {
    search()
  }
}

const init = () => {
  if (!props.text) return
  state.offset = props.text.length
  cleanUp()
  if (props.autoResize && textClampRef.value) {
    addListener(textClampRef.value, update)
    state.unregisterResizeCallback = () => {
      textClampRef.value && removeListener(textClampRef.value, update)
    }
  }
  update()
}
const cleanUp = () => {
  state.unregisterResizeCallback?.()
}

const isOverflow = () => {
  if (!props.maxLines && !props.maxHeight) return false
  if (!textClampRef.value) return false
  if (props.maxLines && getLines() > props.maxLines) return true
  if (props.maxHeight && textClampRef.value.scrollHeight > textClampRef.value.offsetHeight) return true
  return false
}
const getLines = () => {
  if (!contentRef.value) return 0
  // see https://developer.mozilla.org/zh-CN/docs/Web/API/Element/getClientRects
  return Object.keys(
    Array.prototype.slice.call(contentRef.value.getClientRects()).reduce((prev, { top, bottom }) => {
      const key = `${top}/${bottom}`
      if (!prev[key]) {
        prev[key] = true
      }
      return prev
    }, {})
  ).length
}
const search = (...range: number[]) => {
  const [from = 0, to = state.offset] = range
  if (to - from <= 3) {
    stepToFit()
    return
  }
  const target = Math.floor((to + from) / 2)
  clampAt(target)
  if (isOverflow()) {
    search(from, target)
  } else {
    search(target, to)
  }
}
const clampAt = (offset: number) => {
  state.offset = offset
  applyChange()
}
const stepToFit = () => {
  fill()
  clamp()
}
const fill = () => {
  while ((!isOverflow() || getLines() < 2) && state.offset < props.text.length) {
    moveEdge(1)
  }
}
const clamp = () => {
  while (isOverflow() && getLines() > 1 && state.offset > 0) {
    moveEdge(-1)
  }
}
const moveEdge = (steps: number) => {
  clampAt(state.offset + steps)
}

const isClamped = computed(() => {
  if (!props.text) return false
  return state.offset !== props.text.length
})
watch(
  () => isClamped.value,
  (val: boolean) => {
    nextTick(() => {
      emits('clamp-change', val)
    })
  },
  { immediate: true }
)
const realText = computed(() => {
  return isClamped.value ? clampedText.value : props.text
})
const clampedText = computed(() => {
  if (props.location === 'start') {
    return props.ellipsis + (props.text.slice(0, state.offset) || '').trim()
  }
  if (props.location === 'middle') {
    const split = Math.floor(state.offset / 2)
    return (props.text.slice(0, split) || '').trim() + props.ellipsis + (props.text.slice(-split) || '').trim()
  }
  return (props.text.slice(0, state.offset) || '').trim() + props.ellipsis
})

const expand = () => {
  state.localExpanded = true
}
const collapse = () => {
  state.localExpanded = false
}
const toggle = () => {
  state.localExpanded = !state.localExpanded
}
watch(
  () => props.expanded,
  (val: boolean) => {
    state.localExpanded = val
  }
)
watch(
  () => state.localExpanded,
  (val: boolean) => {
    if (val) {
      clampAt(props.text.length)
    } else {
      update()
    }
    if (props.expanded !== val) {
      emits('update:expanded', val)
    }
  }
)

watch(
  () => [props.maxLines, props.maxHeight, props.ellipsis, props.location, isClamped.value].join(),
  () => {
    nextTick(() => {
      update()
    })
  }
)
onUnmounted(() => {
  cleanUp()
})
onMounted(() => {
  init()
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "textClampRef",
    ref: textClampRef,
    class: "text-clamp",
    style: _normalizeStyle({
    overflow: 'hidden',
    maxHeight: realMaxHeight.value,
  })
  }, [
    _createElementVNode("span", {
      ref_key: "contentRef",
      ref: contentRef
    }, [
      _createElementVNode("span", {
        ref_key: "textRef",
        ref: textRef,
        "aria-label": _ctx.text
      }, null, 8, _hoisted_1),
      _renderSlot(_ctx.$slots, "after", {
        expand: expand,
        collapse: collapse,
        toggle: toggle,
        clamped: isClamped.value,
        expanded: state.localExpanded
      })
    ], 512)
  ], 4))
}
}

})